// src/client/components/projects/ProjectTeamCard.tsx
import { X } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { Card } from '@/client/components/ui/card';
import { InitialsAvatar } from '@/client/components/common/InitialsAvatar';
import { MembersDialog } from './MembersDialog';
import type { RouterOutputs } from '@/client/utils/trpc';

type Member = RouterOutputs['projects']['byId']['members'][number];
type Employee = RouterOutputs['employees']['list'][number];

interface Props {
  members: Member[];
  availableEmployees: Employee[];
  isAdmin: boolean;
  managingOpen: boolean;
  onManagingOpenChange: (open: boolean) => void;
  onAddMember: (employeeId: string) => void;
  onRemoveMember: (employeeId: string) => void;
  isAddPending: boolean;
  isRemovePending: boolean;
  onSetMemberRate: (employeeId: string, rate: number | null) => void;
  isSetRatePending: boolean;
}

export function ProjectTeamCard({
  members,
  availableEmployees,
  isAdmin,
  managingOpen,
  onManagingOpenChange,
  onAddMember,
  onRemoveMember,
  isAddPending,
  isRemovePending,
  onSetMemberRate,
  isSetRatePending,
}: Props) {
  return (
    <Card className="overflow-hidden p-0">
      <div className="flex items-center justify-between border-b px-5 py-4">
        <h3 className="font-semibold">Team & Members</h3>
        {isAdmin && (
          <Button variant="ghost" size="sm" onClick={() => onManagingOpenChange(true)}>
            + Assign
          </Button>
        )}
      </div>
      {isAdmin && members.length > 0 && (
        <div className="flex justify-end px-5 py-1.5 border-b bg-muted/30">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground w-24 text-right pr-9">
            Rate/h
          </span>
        </div>
      )}
      <div>
        {members.length === 0 && (
          <p className="px-5 py-4 text-sm text-muted-foreground">No members yet.</p>
        )}
        {members.map((member) => (
          <div
            key={member.id}
            className="flex items-center gap-3 border-b px-5 py-3 last:border-b-0"
          >
            <InitialsAvatar name={member.full_name || 'Unknown'} size={32} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{member.full_name || '—'}</p>
              <p className="truncate text-xs text-muted-foreground">{member.job_title || '—'}</p>
            </div>
            {isAdmin && (
              <div className="relative flex-shrink-0">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                  €
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={member.project_rate ?? ''}
                  placeholder="—"
                  disabled={isSetRatePending}
                  onBlur={(e) => {
                    const val = e.target.value.trim();
                    onSetMemberRate(member.id, val === '' ? null : Number(val));
                  }}
                  className="w-24 pl-5 pr-2 py-1 text-xs text-right rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
                />
              </div>
            )}
            {isAdmin && (
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remove ${member.full_name || 'member'}`}
                className="h-6 w-6 text-muted-foreground hover:text-destructive flex-shrink-0"
                disabled={isRemovePending}
                onClick={() => onRemoveMember(member.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        ))}
      </div>
      <MembersDialog
        open={managingOpen}
        onOpenChange={onManagingOpenChange}
        members={members}
        availableEmployees={availableEmployees}
        isLoading={false}
        onAddMember={onAddMember}
        onRemoveMember={onRemoveMember}
        isAddPending={isAddPending}
        isRemovePending={isRemovePending}
      />
    </Card>
  );
}
